import * as React from 'react'
import { useState, useEffect } from 'react'
import clx from 'classnames'

export type Option = {
  key?: string|number
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
}

type Props = {
  label?: string
  placeholder?: string
  icon?: React.ElementType
  options: Array<Option>
  className?: string
  value?: string | null
  error?: string | boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
  handleChange?: (value: string, props: { name: string, option: Option }) => void
  name?: string
  disabled?: boolean
  children?: React.ReactNode
  orientation?: 'row' | 'column'
}

export const FieldsetRadio = ({
  label,
  value,
  options,
  name,
  icon: GroupIcon,
  disabled,
  handleChange,
  onChange,
  error,
  className,
  children,
  orientation = 'column',
}: Props) => {
  const [storedValue, setStoredValue] = useState<string>(value ?? '')

  useEffect(() => {
    if (value !== undefined && value !== storedValue) {
      setStoredValue(value ?? '')
    }
  }, [value])

  const select = (option: Option) => {
    if (disabled) return
    setStoredValue(option.value)

    if (handleChange) handleChange(option.value, { option, name })

    if (onChange) {
      const event = {
        target: {
          name,
          value: option.value,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      onChange(event)
    }
  }

  const layoutCls =
    orientation === 'row'
      ? 'tw-flex tw-flex-row tw-flex-wrap tw-gap-4'
      : 'tw-flex tw-flex-col tw-gap-2'

  return (
    <fieldset className={clx('tw-w-full', className)}>
      {(label || GroupIcon) && (
        <legend className="tw-flex tw-items-center tw-gap-2 tw-mb-1 tw-text-sm tw-font-medium">
          {GroupIcon && <GroupIcon className="tw-h-4 tw-w-4" />}
          {label}
        </legend>
      )}

      <div className={layoutCls}>
        {options.map((option) => {
          const OptionIcon = option.icon
          const checked = storedValue === option.value

          return (
            <label
              key={option.value}
              className={clx(
                'tw-flex tw-items-center tw-gap-2 tw-text-sm',
                disabled && 'tw-opacity-60 tw-cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                disabled={disabled}
                checked={checked}
                onChange={() => select(option)}
              />

              {OptionIcon && (
                <OptionIcon className="tw-h-4 tw-w-4 tw-text-content-base" />
              )}

              <span>
                {option.content ?? option.label ?? option.value}
              </span>
            </label>
          )
        })}
      </div>

      {name && (
        <input type="hidden" name={name} value={storedValue ?? ''} readOnly />
      )}

      {error && typeof error === 'string' && (
        <span className="tw-text-xs tw-font-medium tw-text-error">
          {error}
        </span>
      )}

      {children && <div className="tw-mt-1">{children}</div>}
    </fieldset>
  )
}
